import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import {
  getIsWorkspaceAgentScrolledToBottom,
  getIsWorkspaceAgentScrollPinnedAfterScroll,
  getShouldShowWorkspaceAgentJumpToBottom,
  getWorkspaceAgentScrollContainerMetrics,
  getWorkspaceAgentScrollOverflowAnchor,
  getWorkspaceAgentScrollUpwardDeltaPx,
  pinWorkspaceAgentScrollToBottom,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/pinWorkspaceAgentScrollToBottom';

export type UsePrivateWorkspaceAgentMessagesAutoScrollParams = {
  /** After jump smooth scroll ends, align to tail only when not already at bottom. */
  runJumpCatchUpOnEndRef: MutableRefObject<(() => void) | undefined>;
  /** Updated each render; smooth `scrollToIndex` on the tail row for jump-to-bottom. */
  runVirtualizerJumpToBottomRef: MutableRefObject<(() => void) | undefined>;
  /** Following the conversation means tracking whichever of these landed last. */
  activeRunId: string | null;
  /** Send-committed working row — follow it even before WSS `Run.started`. */
  isAwaitingRunStart: boolean;
  /**
   * True when the newest row is the caller's optimistic send. Catch-up must not yank someone
   * reading history, but a send they just made should re-arm stick-to-bottom so they see it.
   */
  isLastMessageOwn: boolean;
  /**
   * The hybrid handoff parks "Now it's your turn" at the top of the window. Stick-to-bottom would
   * pull that line back down into the empty space the live prompt is about to fill.
   */
  isPinSuspended: boolean;
  lastElicitationId: string | null;
  lastMessageId: string | null;
  /** Thread switch resets to an instant pin at the latest message. */
  sessionId: string | null;
};

export type UsePrivateWorkspaceAgentMessagesAutoScrollResult = {
  catchUpPinAfterSmoothRef: MutableRefObject<(() => void) | undefined>;
  isPinnedToBottomRef: MutableRefObject<boolean | undefined>;
  isProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  isSmoothProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  jumpToBottom: () => void;
  pinIfFollowingBottom: () => void;
  programmaticPinFrameRef: MutableRefObject<number | undefined>;
  scrollContainerRef: MutableRefObject<HTMLElement | null>;
  showJumpToBottom: boolean;
  suppressMeasureTailPinRef: MutableRefObject<boolean | undefined>;
};

type PinThreadToBottomParams = {
  behavior: ScrollBehavior;
  container: HTMLElement;
};

type ProgrammaticPinFrameRef = {
  current: number | undefined;
};

type SmoothProgrammaticPinCleanupRef = {
  current: (() => void) | undefined;
};

const WORKSPACE_AGENT_SMOOTH_PROGRAMMATIC_PIN_MAX_MS = 1000;

type SyncWorkspaceAgentScrollOverflowAnchorParams = {
  container: HTMLElement;
  isPinned: boolean;
};

type WorkspaceAgentAutoScrollRunSignals = {
  activeRunJustStarted: boolean;
  awaitingRunJustStarted: boolean;
  isAwaitingToActiveRunHandoff: boolean;
};

type GetWorkspaceAgentAutoScrollRunSignalsParams = {
  activeRunId: string | null;
  isAwaitingRunStart: boolean;
  previousActiveRunId: string | null;
  previousIsAwaitingRunStart: boolean | undefined;
};

type HasWorkspaceAgentAutoScrollTargetParams = {
  activeRunId: string | null;
  isAwaitingRunStart: boolean;
  lastElicitationId: string | null;
  lastMessageId: string | null;
};

type WorkspaceAgentMessagesAutoScrollLayoutEffectRefs = {
  accumulatedUpwardScrollPxRef: MutableRefObject<number>;
  isInstantScrollRef: MutableRefObject<boolean | undefined>;
  isPinnedToBottomRef: MutableRefObject<boolean | undefined>;
  isSmoothProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  previousActiveRunIdRef: MutableRefObject<string | null>;
  previousIsAwaitingRunStartRef: MutableRefObject<boolean | undefined>;
  previousLastMessageIdRef: MutableRefObject<string | null>;
  previousSessionIdRef: MutableRefObject<string | null>;
  programmaticPinFrameRef: ProgrammaticPinFrameRef;
  smoothProgrammaticPinCleanupRef: SmoothProgrammaticPinCleanupRef;
};

type RunWorkspaceAgentMessagesAutoScrollLayoutEffectParams = {
  activeRunId: string | null;
  container: HTMLElement;
  isAwaitingRunStart: boolean;
  isLastMessageOwn: boolean;
  lastElicitationId: string | null;
  lastMessageId: string | null;
  pinThreadToBottom: (params: PinThreadToBottomParams) => void;
  refs: WorkspaceAgentMessagesAutoScrollLayoutEffectRefs;
  sessionId: string | null;
};

type WorkspaceAgentMessagesAutoScrollListenersRefs = {
  accumulatedUpwardScrollPxRef: MutableRefObject<number>;
  isPinnedToBottomRef: MutableRefObject<boolean | undefined>;
  isProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  isSmoothProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  lastScrollTopRef: MutableRefObject<number>;
  programmaticPinFrameRef: ProgrammaticPinFrameRef;
  smoothProgrammaticPinCleanupRef: SmoothProgrammaticPinCleanupRef;
};

type SetupWorkspaceAgentMessagesAutoScrollListenersParams = {
  container: HTMLElement;
  pinIfFollowingBottom: () => void;
  refs: WorkspaceAgentMessagesAutoScrollListenersRefs;
  syncJumpToBottomVisibility: () => void;
};

type SyncWorkspaceAgentPinnedFromUserScrollParams = {
  container: HTMLElement;
  refs: WorkspaceAgentMessagesAutoScrollListenersRefs;
};

const cancelScheduledProgrammaticPinClear = (programmaticPinFrameRef: ProgrammaticPinFrameRef) => {
  if (programmaticPinFrameRef.current === undefined) {
    return;
  }

  cancelAnimationFrame(programmaticPinFrameRef.current);
  programmaticPinFrameRef.current = undefined;
};

const cancelSmoothProgrammaticPinEndListener = (smoothProgrammaticPinCleanupRef: SmoothProgrammaticPinCleanupRef) => {
  smoothProgrammaticPinCleanupRef.current?.();
  smoothProgrammaticPinCleanupRef.current = undefined;
};

const clearSmoothProgrammaticPinState = ({
  isSmoothProgrammaticPinRef,
  smoothProgrammaticPinCleanupRef,
}: {
  isSmoothProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  smoothProgrammaticPinCleanupRef: SmoothProgrammaticPinCleanupRef;
}) => {
  isSmoothProgrammaticPinRef.current = false;
  cancelSmoothProgrammaticPinEndListener(smoothProgrammaticPinCleanupRef);
};

const scheduleInstantProgrammaticPinClear = ({
  isProgrammaticPinRef,
  programmaticPinFrameRef,
}: {
  isProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  programmaticPinFrameRef: ProgrammaticPinFrameRef;
}) => {
  cancelScheduledProgrammaticPinClear(programmaticPinFrameRef);

  programmaticPinFrameRef.current = requestAnimationFrame(() => {
    programmaticPinFrameRef.current = requestAnimationFrame(() => {
      programmaticPinFrameRef.current = undefined;
      isProgrammaticPinRef.current = false;
    });
  });
};

type ArmSmoothProgrammaticPinParams = {
  accumulatedUpwardScrollPxRef: MutableRefObject<number>;
  container: HTMLElement;
  isPinnedToBottomRef: MutableRefObject<boolean | undefined>;
  isProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  isSmoothProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  lastScrollTopRef: MutableRefObject<number>;
  onSmoothPinEnd?: () => void;
  programmaticPinFrameRef: ProgrammaticPinFrameRef;
  smoothProgrammaticPinCleanupRef: SmoothProgrammaticPinCleanupRef;
};

const armSmoothProgrammaticPin = ({
  accumulatedUpwardScrollPxRef,
  container,
  isPinnedToBottomRef,
  isProgrammaticPinRef,
  isSmoothProgrammaticPinRef,
  lastScrollTopRef,
  onSmoothPinEnd,
  programmaticPinFrameRef,
  smoothProgrammaticPinCleanupRef,
}: ArmSmoothProgrammaticPinParams) => {
  isPinnedToBottomRef.current = true;
  syncWorkspaceAgentScrollOverflowAnchor({ container, isPinned: true });
  lastScrollTopRef.current = container.scrollTop;
  accumulatedUpwardScrollPxRef.current = 0;
  isProgrammaticPinRef.current = true;
  cancelScheduledProgrammaticPinClear(programmaticPinFrameRef);
  isSmoothProgrammaticPinRef.current = true;
  scheduleSmoothProgrammaticPinClear({
    container,
    isSmoothProgrammaticPinRef,
    onEnd: onSmoothPinEnd,
    smoothProgrammaticPinCleanupRef,
  });
  scheduleInstantProgrammaticPinClear({ isProgrammaticPinRef, programmaticPinFrameRef });
};

const scheduleSmoothProgrammaticPinClear = ({
  container,
  isSmoothProgrammaticPinRef,
  onEnd,
  smoothProgrammaticPinCleanupRef,
}: {
  container: HTMLElement;
  isSmoothProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  onEnd?: () => void;
  smoothProgrammaticPinCleanupRef: SmoothProgrammaticPinCleanupRef;
}) => {
  cancelSmoothProgrammaticPinEndListener(smoothProgrammaticPinCleanupRef);

  const finishSmoothProgrammaticPin = () => {
    clearSmoothProgrammaticPinState({ isSmoothProgrammaticPinRef, smoothProgrammaticPinCleanupRef });
    onEnd?.();
  };

  const onScrollEnd = () => {
    finishSmoothProgrammaticPin();
  };

  container.addEventListener('scrollend', onScrollEnd, { once: true });

  const timeoutId = window.setTimeout(finishSmoothProgrammaticPin, WORKSPACE_AGENT_SMOOTH_PROGRAMMATIC_PIN_MAX_MS);

  smoothProgrammaticPinCleanupRef.current = () => {
    container.removeEventListener('scrollend', onScrollEnd);
    window.clearTimeout(timeoutId);
  };
};

const syncWorkspaceAgentScrollOverflowAnchor = ({
  container,
  isPinned,
}: SyncWorkspaceAgentScrollOverflowAnchorParams) => {
  container.style.overflowAnchor = getWorkspaceAgentScrollOverflowAnchor(isPinned);
};

const getWorkspaceAgentAutoScrollRunSignals = ({
  activeRunId,
  isAwaitingRunStart,
  previousActiveRunId,
  previousIsAwaitingRunStart,
}: GetWorkspaceAgentAutoScrollRunSignalsParams): WorkspaceAgentAutoScrollRunSignals => {
  const activeRunJustStarted =
    activeRunId !== null && activeRunId !== previousActiveRunId && previousIsAwaitingRunStart !== true;
  const awaitingRunJustStarted = isAwaitingRunStart && previousIsAwaitingRunStart !== true;
  const isAwaitingToActiveRunHandoff =
    previousIsAwaitingRunStart === true &&
    !isAwaitingRunStart &&
    activeRunId !== null &&
    activeRunId !== previousActiveRunId;

  return { activeRunJustStarted, awaitingRunJustStarted, isAwaitingToActiveRunHandoff };
};

const hasWorkspaceAgentAutoScrollTarget = ({
  activeRunId,
  isAwaitingRunStart,
  lastElicitationId,
  lastMessageId,
}: HasWorkspaceAgentAutoScrollTargetParams): boolean =>
  Boolean(lastMessageId || lastElicitationId || activeRunId || isAwaitingRunStart);

const runWorkspaceAgentMessagesAutoScrollLayoutEffect = ({
  activeRunId,
  container,
  isAwaitingRunStart,
  isLastMessageOwn,
  lastElicitationId,
  lastMessageId,
  pinThreadToBottom,
  refs,
  sessionId,
}: RunWorkspaceAgentMessagesAutoScrollLayoutEffectParams): (() => void) | undefined => {
  const {
    accumulatedUpwardScrollPxRef,
    isInstantScrollRef,
    isPinnedToBottomRef,
    isSmoothProgrammaticPinRef,
    previousActiveRunIdRef,
    previousIsAwaitingRunStartRef,
    previousLastMessageIdRef,
    previousSessionIdRef,
    programmaticPinFrameRef,
    smoothProgrammaticPinCleanupRef,
  } = refs;

  const didSessionChange = sessionId !== previousSessionIdRef.current;
  previousSessionIdRef.current = sessionId;

  if (didSessionChange) {
    isInstantScrollRef.current = undefined;
    isPinnedToBottomRef.current = true;
    previousActiveRunIdRef.current = null;
    previousIsAwaitingRunStartRef.current = false;
    accumulatedUpwardScrollPxRef.current = 0;
    cancelScheduledProgrammaticPinClear(programmaticPinFrameRef);
    clearSmoothProgrammaticPinState({ isSmoothProgrammaticPinRef, smoothProgrammaticPinCleanupRef });
  }

  const { isAwaitingToActiveRunHandoff } = getWorkspaceAgentAutoScrollRunSignals({
    activeRunId,
    isAwaitingRunStart,
    previousActiveRunId: previousActiveRunIdRef.current,
    previousIsAwaitingRunStart: previousIsAwaitingRunStartRef.current,
  });
  previousActiveRunIdRef.current = activeRunId;
  previousIsAwaitingRunStartRef.current = isAwaitingRunStart;

  if (isAwaitingToActiveRunHandoff) {
    previousLastMessageIdRef.current = lastMessageId;
    return;
  }

  if (
    !hasWorkspaceAgentAutoScrollTarget({
      activeRunId,
      isAwaitingRunStart,
      lastElicitationId,
      lastMessageId,
    })
  ) {
    return;
  }

  const didLastMessageIdChange = lastMessageId !== previousLastMessageIdRef.current;
  previousLastMessageIdRef.current = lastMessageId;

  // Catch-up (and any other growth) must not yank the reader back to the latest message if they
  // have scrolled up into history. A send they just made is the exception — re-arm so they
  // see their own message and the reply that follows. The id must have just changed; a still-
  // pending own row must not re-pin if they scroll up again before the run starts.
  if (isPinnedToBottomRef.current === false && !(isLastMessageOwn && didLastMessageIdChange)) {
    return;
  }

  const behavior: ScrollBehavior = isInstantScrollRef.current === false ? 'smooth' : 'instant';
  isInstantScrollRef.current = false;

  const scrollToBottom = () => {
    pinThreadToBottom({ behavior, container });
  };

  scrollToBottom();

  // scrollHeight can lag the virtualizer sizer by one frame on first paint; a follow-up catches it.
  // Smooth follows must not re-pin on the next frame — that aborts the animation and its scrollend
  // handler would instant catch-up via the virtualizer.
  if (behavior === 'instant') {
    const followUpFrameId = requestAnimationFrame(scrollToBottom);

    return () => {
      cancelAnimationFrame(followUpFrameId);
    };
  }
};

const syncWorkspaceAgentPinnedFromUserScroll = ({ container, refs }: SyncWorkspaceAgentPinnedFromUserScrollParams) => {
  const {
    accumulatedUpwardScrollPxRef,
    isPinnedToBottomRef,
    isProgrammaticPinRef,
    isSmoothProgrammaticPinRef,
    lastScrollTopRef,
  } = refs;
  const metrics = getWorkspaceAgentScrollContainerMetrics(container);
  const isProgrammaticPin = isProgrammaticPinRef.current === true || isSmoothProgrammaticPinRef.current === true;
  const upwardDeltaPx = isProgrammaticPin
    ? 0
    : getWorkspaceAgentScrollUpwardDeltaPx({
        previousAccumulatedPx: accumulatedUpwardScrollPxRef.current,
        previousScrollTop: lastScrollTopRef.current,
        scrollTop: metrics.scrollTop,
      });
  const isPinned = getIsWorkspaceAgentScrollPinnedAfterScroll({
    isAtBottom: getIsWorkspaceAgentScrolledToBottom(metrics),
    isProgrammaticPin,
    upwardDeltaPx,
    wasPinned: isPinnedToBottomRef.current === true,
  });

  accumulatedUpwardScrollPxRef.current = upwardDeltaPx;
  lastScrollTopRef.current = metrics.scrollTop;
  isPinnedToBottomRef.current = isPinned;
  syncWorkspaceAgentScrollOverflowAnchor({ container, isPinned });
};

const setupWorkspaceAgentMessagesAutoScrollListeners = ({
  container,
  pinIfFollowingBottom,
  refs,
  syncJumpToBottomVisibility,
}: SetupWorkspaceAgentMessagesAutoScrollListenersParams): (() => void) => {
  const {
    accumulatedUpwardScrollPxRef,
    isPinnedToBottomRef,
    isProgrammaticPinRef,
    isSmoothProgrammaticPinRef,
    lastScrollTopRef,
    programmaticPinFrameRef,
    smoothProgrammaticPinCleanupRef,
  } = refs;

  lastScrollTopRef.current = container.scrollTop;
  syncWorkspaceAgentScrollOverflowAnchor({
    container,
    isPinned: isPinnedToBottomRef.current !== false,
  });

  const syncPinnedFromUserScroll = () => {
    syncWorkspaceAgentPinnedFromUserScroll({ container, refs });
    syncJumpToBottomVisibility();
  };

  const onResize = () => {
    pinIfFollowingBottom();
    syncJumpToBottomVisibility();
  };

  container.addEventListener('scroll', syncPinnedFromUserScroll, { passive: true });
  // Thumbnails paint after clip metadata; `load` is the signal that an img actually took space.
  container.addEventListener('load', pinIfFollowingBottom, true);

  const observer = new ResizeObserver(onResize);

  observer.observe(container);
  syncJumpToBottomVisibility();

  // Virtualizer sizer growth lives under the message log — not the live-announcement span that is
  // `firstElementChild`, which never resizes when estimated row heights are corrected.
  const messageListLog = container.querySelector('[role="log"]');

  if (messageListLog) {
    observer.observe(messageListLog);
  }

  return () => {
    container.removeEventListener('scroll', syncPinnedFromUserScroll);
    container.removeEventListener('load', pinIfFollowingBottom, true);
    observer.disconnect();
    cancelScheduledProgrammaticPinClear(programmaticPinFrameRef);
    clearSmoothProgrammaticPinState({ isSmoothProgrammaticPinRef, smoothProgrammaticPinCleanupRef });
    isProgrammaticPinRef.current = false;
    accumulatedUpwardScrollPxRef.current = 0;
  };
};

/**
 * Keeps the newest message, live run loader, or prompt in view as the conversation grows.
 *
 * `scrollTo` runs on the chat section itself rather than `scrollIntoView` on a sentinel at the
 * bottom of it — when the section fits its content, `scrollIntoView` walks up to the body and
 * scrolls the page. The fixed panel then slides up under the workspace top bar and obscures
 * its own header. `scrollTo` is a no-op when there's nothing to scroll, so the body never
 * leaks.
 *
 * Layout effect (vs useEffect) so the scroll lands before the first paint when the chat
 * section remounts after returning from tasks.
 */
export const usePrivateWorkspaceAgentMessagesAutoScroll = ({
  activeRunId,
  isAwaitingRunStart,
  isLastMessageOwn,
  isPinSuspended,
  lastElicitationId,
  lastMessageId,
  runJumpCatchUpOnEndRef,
  runVirtualizerJumpToBottomRef,
  sessionId,
}: UsePrivateWorkspaceAgentMessagesAutoScrollParams): UsePrivateWorkspaceAgentMessagesAutoScrollResult => {
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const catchUpPinAfterSmoothRef = useRef<(() => void) | undefined>(undefined);
  const isPinnedToBottomRef = useRef<boolean>();
  const isSmoothProgrammaticPinRef = useRef<boolean>();
  const suppressMeasureTailPinRef = useRef<boolean>();
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const isProgrammaticPinRef = useRef<boolean>();
  const programmaticPinFrameRef = useRef<number>();
  const smoothProgrammaticPinCleanupRef = useRef<() => void>();
  const lastScrollTopRef = useRef(0);
  const accumulatedUpwardScrollPxRef = useRef(0);
  const isInstantScrollRef = useRef<boolean>();
  const previousActiveRunIdRef = useRef<string | null>(null);
  const previousIsAwaitingRunStartRef = useRef<boolean>();
  const previousLastMessageIdRef = useRef<string | null>(null);
  const previousSessionIdRef = useRef<string | null>(null);

  const pinThreadToBottom = useCallback(({ behavior, container }: PinThreadToBottomParams) => {
    // Layout-effect rAF follow-ups and duplicate smooth calls must not tear down an in-flight follow.
    if (isSmoothProgrammaticPinRef.current === true && behavior === 'smooth') {
      return;
    }

    if (behavior === 'instant' || isSmoothProgrammaticPinRef.current === true) {
      clearSmoothProgrammaticPinState({ isSmoothProgrammaticPinRef, smoothProgrammaticPinCleanupRef });
    }

    const { didPin } = pinWorkspaceAgentScrollToBottom(container, behavior);
    isPinnedToBottomRef.current = true;
    syncWorkspaceAgentScrollOverflowAnchor({ container, isPinned: true });
    lastScrollTopRef.current = container.scrollTop;

    if (!didPin) {
      return;
    }

    if (behavior === 'smooth') {
      armSmoothProgrammaticPin({
        accumulatedUpwardScrollPxRef,
        container,
        isPinnedToBottomRef,
        isProgrammaticPinRef,
        isSmoothProgrammaticPinRef,
        lastScrollTopRef,
        onSmoothPinEnd: () => catchUpPinAfterSmoothRef.current?.(),
        programmaticPinFrameRef,
        smoothProgrammaticPinCleanupRef,
      });
      return;
    }

    accumulatedUpwardScrollPxRef.current = 0;
    isProgrammaticPinRef.current = true;
    cancelScheduledProgrammaticPinClear(programmaticPinFrameRef);
    scheduleInstantProgrammaticPinClear({ isProgrammaticPinRef, programmaticPinFrameRef });
  }, []);

  const pinIfFollowingBottom = useCallback(() => {
    if (isPinSuspended || isPinnedToBottomRef.current === false) {
      return;
    }

    const container = scrollContainerRef.current;

    if (container === null) {
      return;
    }

    // An instant observer pin in the same frame as a smooth follow cancels that animation.
    if (isProgrammaticPinRef.current || isSmoothProgrammaticPinRef.current) {
      return;
    }

    pinThreadToBottom({ behavior: 'instant', container });
  }, [isPinSuspended, pinThreadToBottom]);

  const syncJumpToBottomVisibility = useCallback(() => {
    const container = scrollContainerRef.current;

    if (container === null) {
      setShowJumpToBottom(false);
      return;
    }

    setShowJumpToBottom(getShouldShowWorkspaceAgentJumpToBottom(getWorkspaceAgentScrollContainerMetrics(container)));
  }, []);

  const jumpToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    const scrollToTail = runVirtualizerJumpToBottomRef.current;

    if (container === null || scrollToTail === undefined) {
      return;
    }

    if (isSmoothProgrammaticPinRef.current === true) {
      return;
    }

    suppressMeasureTailPinRef.current = true;
    scrollToTail();
    armSmoothProgrammaticPin({
      accumulatedUpwardScrollPxRef,
      container,
      isPinnedToBottomRef,
      isProgrammaticPinRef,
      isSmoothProgrammaticPinRef,
      lastScrollTopRef,
      onSmoothPinEnd: () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            runJumpCatchUpOnEndRef.current?.();
            suppressMeasureTailPinRef.current = false;
          });
        });
      },
      programmaticPinFrameRef,
      smoothProgrammaticPinCleanupRef,
    });
  }, [runJumpCatchUpOnEndRef, runVirtualizerJumpToBottomRef]);

  useIsomorphicLayoutEffect(() => {
    const container = scrollContainerRef.current;

    if (container === null || isPinSuspended) {
      if (isPinSuspended) {
        isPinnedToBottomRef.current = false;
      }

      return;
    }

    return runWorkspaceAgentMessagesAutoScrollLayoutEffect({
      activeRunId,
      container,
      isAwaitingRunStart,
      isLastMessageOwn,
      lastElicitationId,
      lastMessageId,
      pinThreadToBottom,
      refs: {
        accumulatedUpwardScrollPxRef,
        isInstantScrollRef,
        isPinnedToBottomRef,
        isSmoothProgrammaticPinRef,
        previousActiveRunIdRef,
        previousIsAwaitingRunStartRef,
        previousLastMessageIdRef,
        previousSessionIdRef,
        programmaticPinFrameRef,
        smoothProgrammaticPinCleanupRef,
      },
      sessionId,
    });
  }, [
    activeRunId,
    isAwaitingRunStart,
    isLastMessageOwn,
    isPinSuspended,
    lastElicitationId,
    lastMessageId,
    pinThreadToBottom,
    sessionId,
  ]);

  // The composer sits below the thread in a flex column. TipTap mounts asynchronously, so the
  // chat viewport can shrink a few hundred ms after history first scrolls to the bottom. Asset
  // pills also swap fallback labels for names/thumbnails after Clips.listByIds — that reflows
  // markdown and grows content *above* the last message. Re-pin while still stuck to the bottom;
  // only an upward user scroll releases the flag.
  useEffect(() => {
    const container: HTMLElement | null = scrollContainerRef.current;

    if (!container) {
      return;
    }

    return setupWorkspaceAgentMessagesAutoScrollListeners({
      container,
      pinIfFollowingBottom,
      refs: {
        accumulatedUpwardScrollPxRef,
        isPinnedToBottomRef,
        isProgrammaticPinRef,
        isSmoothProgrammaticPinRef,
        lastScrollTopRef,
        programmaticPinFrameRef,
        smoothProgrammaticPinCleanupRef,
      },
      syncJumpToBottomVisibility,
    });
  }, [pinIfFollowingBottom, syncJumpToBottomVisibility]);

  return {
    catchUpPinAfterSmoothRef,
    isPinnedToBottomRef,
    isProgrammaticPinRef,
    isSmoothProgrammaticPinRef,
    jumpToBottom,
    pinIfFollowingBottom,
    programmaticPinFrameRef,
    scrollContainerRef,
    showJumpToBottom,
    suppressMeasureTailPinRef,
  };
};
